import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import MaldivesWasPriceRow from './MaldivesWasPriceRow'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import type { CalendarItemBundleData } from '../hooks/types'
import useCalendarItemPricing from './useCalendarItemPricing'
import MaldivesFnfSpecialOffer from './MaldivesFnfSpecialOffer'
import MaldivesCharterRewardSeatsAvailableLabel from './MaldivesCharterRewardSeatsAvailableLabel'

// Utils
import { getCTAMessage } from '../hooks/utils'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import Places from 'constants/places'
import config from 'constants/config'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { mergeOccupants } from 'lib/offer/occupancyUtils'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import type { FlightsClassTypes } from 'constants/flight'
import { getMaldivesMobileCalendarPriceContextLabels } from './maldivesMobileCalendarPriceContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  bundleData: CalendarItemBundleData
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  /** When provided (offer page), Kids Fly Free label is shown only if offer has campaign tag and date qualifies */
  offer?: { campaignTags?: Array<string> }
}

// Styled Components - Mobile specific layout
const DateCard = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const SelectButton = styled(TextButton)`
  min-width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesOfferCalendarItemMobile(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    bundleData,
    rooms,
    fareClass,
    offer,
  } = props

  const intl = useIntl()
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const { isKidsFlyFreeDate, shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const showRewardSeatsLabel = useCharterRewardAvailabilityContext().isAvailableForDate(checkIn)

  // Merge multiple rooms into single occupancy for search params
  const mergedRoom = rooms.length > 0 ? mergeOccupants(rooms) : { adults: 2, children: 0, childrenAge: [] }
  const adultsCount = mergedRoom.adults
  const childrenParam = encodeChildrenOccupancy(mergedRoom.childrenAge)

  const {
    regularPrice,
    memberPrice,
    isMemberPrice,
    displayPrice,
    wasPrice,
    shouldShowWasPrice,
    inventoryMessageType,
    inventoryMessage,
    fnfInventoryMessage,
  } = useCalendarItemPricing({
    bundleData,
    luxPlusEnabled,
    priceMode,
  })

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const selectText = intl.formatMessage({
    id: 'MvGwxY9AMe6ETPfczwZK',
    defaultMessage: 'Select',
    description: 'Button label to select a charter flight date in the flights flow',
  })
  const unavailableText = intl.formatMessage({
    id: 'MkdIPUtrVEQzhLPF4i4b',
    defaultMessage: 'Unavailable',
    description: 'Button label when charter flight date is unavailable',
  })
  const noPackageText = intl.formatMessage({
    id: 'J7ASvqQn0CMBMT9iseru',
    defaultMessage: 'No package available',
    description: 'Text shown when no hotel package is available for charter flight date',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const { nightsText, priceContextText } = getMaldivesMobileCalendarPriceContextLabels(
    intl,
    nights,
    priceMode,
    adultsCount,
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5TknjYVpdoEi',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })

  // Render price display
  let priceDisplay
  if (bundleData.hasNoPackage) {
    priceDisplay = (
      <BodyText variant="small" colour="light-foreground-alt">
        {noPackageText}
      </BodyText>
    )
  } else if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="light-foreground-alt">
        {loadingText}
      </BodyText>
    )
  } else if (displayPrice !== undefined && displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <div>
          <FormattedMessage
            id="WvYmKxlzM32P__CnctBk"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives offer calendar mobile"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'light-foreground-alt'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
        {shouldShowWasPrice && wasPrice != null && <MaldivesWasPriceRow
          altPrice={wasPrice}
          saleUnit={priceMode === 'perPerson' ? '/person' : undefined}
        />}
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="light-foreground-alt">
        {unavailableText}
      </BodyText>
    )
  }

  const ctaMessage = getCTAMessage(bundleData.seatsLeft, bundleData.roomsLeft)

  // Render select button
  const selectButton = (isSelected && bundleData.available) ? (
    <SelectButton
      kind="primary"
      nonInteractable
      tabIndex={-1}
    >
      {selectedText}
    </SelectButton>
  ) : (
    <SelectButton
      kind="secondary"
      onClick={() => onSelect(option)}
      disabled={!bundleData.available || bundleData.hasNoPackage}
    >
      {bundleData.available ? selectText : unavailableText}
    </SelectButton>
  )

  // Pre-compute CTA content based on seat/room availability
  let ctaContent: React.ReactNode
  if (ctaMessage === 'hotel-sold-out') {
    ctaContent = (
      <Group direction="horizontal" horizontalAlign="end" verticalAlign="center">
        <BodyText variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="67iYmaeI_4LikZXraHif"
            defaultMessage="<link>View similar bundles</link> for these dates"
            description="CTA when hotel is sold out, links to search for similar Maldives bundles"
            values={{
              link: (chunks) => (
                <TextLink
                  weight="regular"
                  to={`/search?${buildSearchParamsForFilterPane({
                    originalSearch: '',
                    destinationName: Places.Maldives.name,
                    destinationId: Places.Maldives.id,
                    adults: adultsCount,
                    children: childrenParam,
                  })}&checkIn=${checkIn}&checkOut=${checkOut}`}
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </BodyText>
      </Group>
    )
  } else if (ctaMessage === 'bundle-sold-out') {
    ctaContent = (
      <Group direction="horizontal" horizontalAlign="end" verticalAlign="center">
        <BodyText variant="medium" colour="light-foreground-alt">
          <FormattedMessage
            id="xrX71nYk8QpV6O_5s-kR"
            defaultMessage="Call our team on <phoneLink>{phoneNumber}</phoneLink> for more options"
            description="CTA when charter bundle is sold out, provides phone number for support"
            values={{
              phoneLink: (chunks) => (
                <TextLink weight="regular" to={`tel:${config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}`}>
                  {chunks}
                </TextLink>
              ),
              phoneNumber: config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER,
            }}
          />
        </BodyText>
      </Group>
    )
  } else {
    ctaContent = (
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
        <VerticalSpacer gap={4}>
          <BodyText variant="small" colour="light-foreground-alt">
            {priceContextText}
          </BodyText>
          {priceDisplay}
        </VerticalSpacer>
        {selectButton}
      </Group>
    )
  }

  return (
    <DateCard>
      <VerticalSpacer gap={12}>
        {/* Dates Row */}
        <DateRangeContainer>
          <div>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatDate(departDate, INTL_LONG_WEEKDAY)}
            </BodyText>
            <Heading variant="heading5" wrap="no-wrap">
              {intl.formatDate(departDate, INTL_DMY_CASUAL_FORMAT)}
            </Heading>
          </div>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <LineArrowRightIcon size="M" />
            <BodyText variant="small" colour="light-foreground-alt">
              {nightsText}
            </BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end">
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatDate(checkOut, INTL_LONG_WEEKDAY)}
              </BodyText>
              <Heading variant="heading5" wrap="no-wrap">
                {intl.formatDate(checkOut, INTL_DMY_CASUAL_FORMAT)}<SmallSup>{dayOffsetText}</SmallSup>
              </Heading>
            </Group>
          </Group>
        </DateRangeContainer>

        {/* Kids Fly Free + Availability Warning */}
        <Group direction="vertical" horizontalAlign="start" gap={0}>
          {ctaMessage === 'none' && (!bundleData.isFnfPriceApplied || inventoryMessageType !== 'seat') && inventoryMessage !== '' && (
            <Group direction="horizontal" verticalAlign="center" gap={4}>
              <SolidExclamationCircleIcon colour="urgency" size="XS" />
              <BodyText variant="small" colour="critical">
                {inventoryMessage}
              </BodyText>
            </Group>
          )}
          {ctaMessage === 'none' && bundleData.isFnfPriceApplied && (
            <MaldivesFnfSpecialOffer
              fnfInventoryMessage={fnfInventoryMessage}
              isInfiniteMember={isInfiniteMember}
            />
          )}
          {(offer ? shouldShowKidsFlyFreeForOffer(offer, checkIn) : isKidsFlyFreeDate(departDate)) && <KidsFlyFreeLabel variant="short" />}
          {showRewardSeatsLabel && <MaldivesCharterRewardSeatsAvailableLabel />}
        </Group>

        {/* Price and Select Button OR CTA */}
        {ctaContent}
      </VerticalSpacer>
    </DateCard>
  )
}

export default MaldivesOfferCalendarItemMobile
